/**
 * LegalNameRequiredModal — global blocking modal that fires when the
 * authenticated user is missing legal first/last name on their profile.
 *
 * Mounts at the App root and auto-shows whenever
 * `profile.first_name` or `profile.last_name` is empty. The user cannot
 * dismiss it (no close button, click-outside disabled, ESC suppressed
 * by Radix when no close affordance is wired) — the only way out is
 * saving a valid first + last name.
 *
 * Triggered most commonly after the first Google OAuth login when
 * `raw_user_meta_data` only carried a single-token name we couldn't
 * split (see `deriveNamesFromOAuthMetadata` in the backend), and on any
 * legacy account whose profile was created before the OAuth name-split
 * fix landed.
 */

import { useEffect, useState } from 'react';
import { Loader2, IdCard } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import { useUpdateUserProfiles } from '@/api/generated_hooks/generated-user_profiles-hooks';
import { useToast } from '@/contexts/ToastContext';

function isLegalNameMissing(profile: { first_name?: string | null; last_name?: string | null } | null): boolean {
  if (!profile) return false;
  const first = (profile.first_name ?? '').trim();
  const last = (profile.last_name ?? '').trim();
  return !first || !last;
}

export function LegalNameRequiredModal() {
  const { isAuthenticated, profile, updateProfile } = useAuthStore();
  const updateMutation = useUpdateUserProfiles();
  const { showError } = useToast();

  const open = isAuthenticated && isLegalNameMissing(profile ?? null);

  // Keep field state in component so partial typing isn't lost when the
  // store changes underneath (e.g. a stale /auth/session refresh).
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayFirstName, setDisplayFirstName] = useState('');
  const [displayLastName, setDisplayLastName] = useState('');
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string }>({});

  // Pre-fill from profile each time the modal opens so partial values
  // (e.g. first_name set but last_name empty) appear in the inputs.
  useEffect(() => {
    if (open) {
      setFirstName((profile?.first_name ?? '').trim());
      setLastName((profile?.last_name ?? '').trim());
      setDisplayFirstName((profile?.display_first_name ?? '').trim());
      setDisplayLastName((profile?.display_last_name ?? '').trim());
      setErrors({});
    }
  }, [open, profile?.first_name, profile?.last_name, profile?.display_first_name, profile?.display_last_name]);

  if (!open || !profile?.id) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const nextErrors: { firstName?: string; lastName?: string } = {};
    if (!trimmedFirst) nextErrors.firstName = 'Required';
    if (!trimmedLast) nextErrors.lastName = 'Required';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      // Always patch display_* — blank → null. The user must be able to
      // CLEAR a previously-set display name from this modal. If we only
      // wrote when the field had a value, a user who emptied the input
      // would silently see their old display name persist after save.
      const trimmedDisplayFirst = displayFirstName.trim();
      const trimmedDisplayLast = displayLastName.trim();
      const updates: Record<string, string | null> = {
        first_name: trimmedFirst,
        last_name: trimmedLast,
        display_first_name: trimmedDisplayFirst || null,
        display_last_name: trimmedDisplayLast || null,
      };

      const result = await updateMutation.mutateAsync({
        id: profile.id,
        data: updates as any,
      });
      // Merge result into store so the gate (which reads from store)
      // immediately re-evaluates and unmounts the modal.
      updateProfile({ ...profile, ...(result as any) });
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to save name');
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        hideClose
        closeOnOutsideClick={false}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="sm:max-w-md"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary">
            <IdCard className="h-4 w-4" />
            <DialogTitle>Complete your name</DialogTitle>
          </div>
          <DialogDescription>
            Before you can use HighCall we need your legal first and last
            name. Display names are optional and shown publicly instead of
            your legal name.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">
                Legal First Name <span className="text-destructive">*</span>
              </Label>
              <Input
                autoFocus
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                disabled={updateMutation.isPending}
                className={errors.firstName ? 'border-destructive' : ''}
                aria-invalid={!!errors.firstName}
              />
              {errors.firstName && (
                <p className="text-xs text-destructive mt-1">{errors.firstName}</p>
              )}
            </div>
            <div>
              <Label className="text-sm">
                Legal Last Name <span className="text-destructive">*</span>
              </Label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                disabled={updateMutation.isPending}
                className={errors.lastName ? 'border-destructive' : ''}
                aria-invalid={!!errors.lastName}
              />
              {errors.lastName && (
                <p className="text-xs text-destructive mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground">
                Display First Name
              </Label>
              <Input
                value={displayFirstName}
                onChange={(e) => setDisplayFirstName(e.target.value)}
                disabled={updateMutation.isPending}
                placeholder="Optional"
              />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">
                Display Last Name
              </Label>
              <Input
                value={displayLastName}
                onChange={(e) => setDisplayLastName(e.target.value)}
                disabled={updateMutation.isPending}
                placeholder="Optional"
              />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save and continue'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
